import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  CandidataCau,
  FormatoCau,
  MotivoCau,
  ProrrogaResumen,
  TipoSolicitudCau,
} from '@manejo-alcanza/contracts';
import {
  candidatasCau,
  crearProrroga,
  descargarCau,
  fecha,
  listarOrdenes,
  listarProrrogas,
  marcarRadicada,
  motivosCau,
  registrarRespuestaCau,
} from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Tabla } from '../componentes/basicos';

/**
 * F3 — el CAU: pedir una prórroga o un anticipo al Grupo Éxito.
 *
 * **Lo valioso no es el archivo: es el registro.** Cinco de los nueve motivos
 * del catálogo oficial son responsabilidad del Éxito, y guardarlos con su
 * responsabilidad es lo que después permite demostrar cuánto del incumplimiento
 * no fue de Alcanza.
 *
 * Tres momentos distintos, y la pantalla los distingue: **generar** el archivo,
 * **radicarlo** en la PQR —eso lo hace una persona— y **registrar** lo que
 * contestaron.
 */
export function Cau(): ReactNode {
  const consulta = useQuery({ queryKey: ['cau'], queryFn: listarProrrogas });
  /*
   * `?orden=` lo pone el panel «Qué día cabe» del expediente (F2) cuando detecta
   * que la orden está pedida fuera de la frecuencia del CEDI. Llegar aquí con la
   * orden ya elegida evita volver a buscarla en una lista de doscientas, que es
   * justo donde se digita la orden equivocada.
   */
  const [parametros] = useSearchParams();
  const desdeLaOrden = parametros.get('orden') ?? '';

  const [creando, setCreando] = useState(desdeLaOrden !== '');
  // La orden que se lleva al formulario: la del enlace, o la que se elija de la
  // lista de trabajo de abajo.
  const [ordenElegida, setOrdenElegida] = useState(desdeLaOrden);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());

  if (consulta.isLoading) return <Cargando que="las solicitudes de prórroga" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const solicitudes = consulta.data!;
  const delExito = solicitudes.filter((s) => s.responsabilidad === 'E').length;
  const sinRadicar = solicitudes.filter((s) => s.estado === 'GENERADA').length;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">CAU</h1>
          <p className="mt-1 text-tenue">
            Solicitudes de prórroga y anticipo ante el Grupo Éxito. Se radican por PQR
            adjuntando el Excel oficial.
          </p>
        </div>
        <Boton onClick={() => setCreando((v) => !v)}>
          {creando ? 'Cancelar' : 'Nueva solicitud'}
        </Boton>
      </header>

      {creando && (
        <Formulario
          key={ordenElegida}
          ordenInicial={ordenElegida}
          alTerminar={() => setCreando(false)}
        />
      )}

      {/*
        La lista de trabajo, justo debajo de «Nueva solicitud».

        El sistema ya sabe que ordenes no se pueden entregar el dia que piden
        —lo calcula F2—, asi que buscarlas a mano en un desplegable de
        doscientas era ponerle a la persona el trabajo que hace la maquina, y
        justo donde se elige la orden equivocada (corregido a peticion de Pedro).
      */}
      <ListaDeTrabajo
        alElegir={(id) => {
          setOrdenElegida(id);
          setCreando(true);
        }}
      />

      {solicitudes.length === 0 ? (
        <Card>
          <p className="text-sm text-tenue">
            No hay ninguna solicitud todavía. Se crea una cuando la fecha que pide una orden
            no se puede cumplir.
          </p>
        </Card>
      ) : (
        <>
          {/*
            El número que justifica llevar esto: cuántas de las prórrogas fueron
            por algo del cliente. Sin registrarlo con su motivo, no hay forma de
            demostrarlo tres meses después.
          */}
          {delExito > 0 && (
            <Aviso>
              <strong>
                {delExito} de {solicitudes.length}{' '}
                {delExito === 1 ? 'solicitud es' : 'solicitudes son'} por algo del Éxito.
              </strong>{' '}
              La responsabilidad la pone el catálogo oficial a partir del motivo, no la elige
              nadie aquí. Es lo que permite demostrar cuánto del incumplimiento no fue de
              Alcanza.
            </Aviso>
          )}

          {elegidas.size > 0 && (
            <Generar
              ids={[...elegidas]}
              alTerminar={() => setElegidas(new Set())}
            />
          )}

          {sinRadicar > 0 && elegidas.size === 0 && (
            <p className="text-sm text-aviso">
              {sinRadicar} {sinRadicar === 1 ? 'solicitud ya tiene' : 'solicitudes ya tienen'}{' '}
              su archivo generado y sin radicar en la PQR.
            </p>
          )}

          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                <th className="px-3 py-2 font-medium" />
                <th className="px-3 py-2 font-medium">Orden</th>
                <th className="px-3 py-2 font-medium">De / a</th>
                <th className="px-3 py-2 font-medium">Motivo</th>
                <th className="px-3 py-2 font-medium">De quién</th>
                <th className="px-3 py-2 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {solicitudes.map((s) => (
                <Fila
                  key={s.id}
                  solicitud={s}
                  elegida={elegidas.has(s.id)}
                  alElegir={() =>
                    setElegidas((e) => {
                      const n = new Set(e);
                      if (n.has(s.id)) n.delete(s.id);
                      else n.add(s.id);
                      return n;
                    })
                  }
                />
              ))}
            </tbody>
          </Tabla>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const NOMBRE_RESPONSABLE: Record<string, string> = {
  E: 'Éxito',
  P: 'Alcanza',
  N: 'Nadie',
};

// ---------------------------------------------------------------------------

const NOMBRES_DE_DIA = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

/**
 * Las órdenes que el sistema detectó que **no se pueden entregar el día que
 * piden**, listas para abrirles un CAU de un clic.
 *
 * Es la lista de trabajo, y es la razón de ser de esta pantalla. Antes había que
 * saberse de memoria cuál orden tenía problema y buscarla en un desplegable de
 * doscientas *(corregido a petición de Pedro, 19/09/2026)*.
 *
 * **No entra lo que el calendario no sabe juzgar.** Una orden de la que no se
 * conoce la frecuencia de ninguna tienda no aparece aquí: meterla en una lista
 * titulada «no se pueden entregar» convertiría un hueco del calendario en una
 * acusación al cliente.
 */
function ListaDeTrabajo({ alElegir }: { alElegir: (ordenId: string) => void }): ReactNode {
  const consulta = useQuery({ queryKey: ['cau-candidatas'], queryFn: candidatasCau });

  if (consulta.isLoading || consulta.isError) return null;
  const candidatas = consulta.data!;

  if (candidatas.length === 0) {
    return (
      <Card titulo="Órdenes que no se pueden entregar el día que piden">
        <p className="text-sm text-tenue">
          Ninguna, por ahora. Aquí aparecen solas las órdenes sin entregar que caen fuera de
          la frecuencia del CEDI del Éxito, y las de cualquier cliente que el camión no
          puede hacer ningún día de su ventana.
        </p>
      </Card>
    );
  }

  // Los dos motivos no se arreglan igual, asi que no se mezclan en una sola
  // lista: uno se defiende con un CAU ante el Exito y el otro es nuestro.
  const conCau = candidatas.filter((c) => c.llevaCau);
  const sinCau = candidatas.filter((c) => !c.llevaCau);
  const sinAtender = conCau.filter((c) => c.solicitudAbierta === null).length;

  return (
    <Card
      titulo={`Órdenes que no se pueden entregar el día que piden (${candidatas.length})`}
    >
      <p className="text-sm text-tenue">
        El sistema las detecta solo, cruzando la fecha de cada orden con el calendario del
        CEDI y con el camión.{' '}
        {sinAtender > 0 && (
          <>
            <strong>{sinAtender}</strong> {sinAtender === 1 ? 'no tiene' : 'no tienen'}{' '}
            solicitud todavía.
          </>
        )}
      </p>

      <ul className="mt-3 space-y-2">
        {conCau.map((c) => (
          <li
            key={c.ordenId}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-borde pt-2"
          >
            <Link
              to={`/ordenes/${c.ordenId}`}
              className="font-medium text-acento hover:underline"
            >
              {c.numero}
            </Link>
            <span className="text-sm">
              {NOMBRES_DE_DIA[c.diaSemana - 1]} {fecha(c.fechaEntrega)}
            </span>
            <span className="text-sm text-tenue">{motivoDeLaCandidata(c)}</span>

            <span className="ml-auto flex items-center gap-2">
              {/*
                Contexto, no impedimento: la regla de los tres dias limita la
                fecha NUEVA que se pide, no la original. Decir «fuera de plazo»
                haria creer que el CAU ya no se puede radicar, y si se puede.
              */}
              {c.entregaVencida && (
                <span
                  className="text-xs text-tenue"
                  title="Se puede pedir la prórroga igual: la regla de los tres días limita la fecha nueva, no la original"
                >
                  la entrega ya pasó
                </span>
              )}
              {c.solicitudAbierta ? (
                // No se esconde: esconderla daria a entender que se resolvio,
                // cuando lo unico que paso es que alguien ya lo vio.
                <span className="rounded-full bg-neutro-tenue px-2 py-0.5 text-[0.7rem] font-semibold text-neutro">
                  YA TIENE SOLICITUD · {c.solicitudAbierta}
                </span>
              ) : (
                <button
                  type="button"
                  className="text-sm underline underline-offset-2"
                  onClick={() => alElegir(c.ordenId)}
                >
                  Pedir prórroga
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>

      <p className="mt-3 text-xs text-tenue">
        El motivo lo eliges tú al crear la solicitud: de él sale la responsabilidad, y esa
        la afirma una persona, no el sistema.
      </p>

      {/*
        El otro motivo, aparte.

        Una orden que el camion no puede hacer tampoco se entrega, y hay que
        verla — pero **no se arregla con un CAU**: el problema es nuestro, no del
        cliente, y radicar una prorroga por algo que es nuestro es justo lo que
        el catalogo de motivos distingue.
      */}
      {sinCau.length > 0 && (
        <div className="mt-4 border-t border-borde pt-3">
          <h4 className="text-sm font-medium">
            Y {sinCau.length} que {sinCau.length === 1 ? 'tampoco sale' : 'tampoco salen'}, pero
            no {sinCau.length === 1 ? 'lleva' : 'llevan'} CAU
          </h4>
          <p className="mt-1 text-xs text-tenue">
            El CAU se radica en la PQR del Grupo Éxito y solo sirve para sus órdenes. Estas
            no se pueden entregar porque <strong>el camión no puede salir</strong>, que es
            problema nuestro: se resuelve moviendo la cita con el cliente.
          </p>
          <ul className="mt-2 space-y-1">
            {sinCau.map((c) => (
              <li key={c.ordenId} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                <Link
                  to={`/ordenes/${c.ordenId}`}
                  className="font-medium text-acento hover:underline"
                >
                  {c.numero}
                </Link>
                <span className="text-tenue">{c.clienteNombre}</span>
                <span>
                  {NOMBRES_DE_DIA[c.diaSemana - 1]} {fecha(c.fechaEntrega)}
                </span>
                <span className="text-tenue">{motivoDeLaCandidata(c)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** Por qué esa orden no se puede entregar ese día, en una línea. */
function motivoDeLaCandidata(c: CandidataCau): string {
  if (c.camion?.estado === 'NO_PUEDE') {
    return c.camion.razon === 'PICO_Y_PLACA'
      ? `el camión tiene pico y placa — ${c.camion.detalle}`
      : `no hay camión — ${c.camion.detalle}`;
  }
  const conocidas = c.reciben + c.noReciben;
  if (c.veredicto === 'PARCIAL') {
    return `el CEDI recibe para ${c.reciben} de ${conocidas} tiendas, no para las otras ${c.noReciben}`;
  }
  return conocidas === 1
    ? 'el CEDI no recibe ese día para su tienda'
    : `el CEDI no recibe ese día para ninguna de sus ${conocidas} tiendas conocidas`;
}

function Fila({
  solicitud,
  elegida,
  alElegir,
}: {
  solicitud: ProrrogaResumen;
  elegida: boolean;
  alElegir: () => void;
}): ReactNode {
  const [abierto, setAbierto] = useState(false);
  const puedeGenerar = solicitud.estado === 'BORRADOR' || solicitud.estado === 'GENERADA';

  return (
    <>
      <tr className="border-b border-borde last:border-0">
        <td className="px-3 py-2">
          <input
            type="checkbox"
            aria-label={`Incluir ${solicitud.ordenNumero} en el archivo`}
            disabled={!puedeGenerar}
            checked={elegida}
            onChange={alElegir}
          />
        </td>
        <td className="px-3 py-2">
          <Link to={`/ordenes/${solicitud.ordenId}`} className="font-medium text-acento hover:underline">
            {solicitud.ordenNumero}
          </Link>
          <div className="text-xs text-tenue">{solicitud.tipoSolicitud.trim().toLowerCase()}</div>
        </td>
        <td className="px-3 py-2 whitespace-nowrap text-sm">
          {fecha(solicitud.fechaOriginal)} → <strong>{fecha(solicitud.fechaPedida)}</strong>
          {solicitud.fechaAprobada && solicitud.fechaAprobada !== solicitud.fechaPedida && (
            /* Contestaron con otra fecha. Es el dato que se pierde en un correo. */
            <div className="text-xs text-aviso">
              dieron el {fecha(solicitud.fechaAprobada)}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-sm">
          {solicitud.motivo.trim()}
          {solicitud.comentario && (
            <div className="text-xs text-tenue">{solicitud.comentario}</div>
          )}
        </td>
        <td className="px-3 py-2">
          {solicitud.responsabilidad === null ? (
            <span className="text-xs italic text-tenue">sin asignar</span>
          ) : (
            <span
              className={`rounded-full px-2 py-0.5 text-[0.7rem] font-semibold ${
                solicitud.responsabilidad === 'E'
                  ? 'bg-acento-tenue text-acento'
                  : solicitud.responsabilidad === 'P'
                    ? 'bg-alerta-tenue text-alerta'
                    : 'bg-neutro-tenue text-neutro'
              }`}
            >
              {NOMBRE_RESPONSABLE[solicitud.responsabilidad]}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-xs">
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            className="text-acento underline hover:no-underline"
          >
            {solicitud.estado.replaceAll('_', ' ').toLowerCase()}
          </button>
          {solicitud.numeroRadicado && (
            <div className="font-mono text-tenue">{solicitud.numeroRadicado}</div>
          )}
        </td>
      </tr>
      {abierto && (
        <tr className="border-b border-borde bg-fondo">
          <td colSpan={6} className="px-3 py-3">
            <Seguimiento solicitud={solicitud} alTerminar={() => setAbierto(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

/** Marcar radicada, y registrar lo que contestaron. Dos momentos distintos. */
function Seguimiento({
  solicitud,
  alTerminar,
}: {
  solicitud: ProrrogaResumen;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const [radicado, setRadicado] = useState(solicitud.numeroRadicado ?? '');
  const [estado, setEstado] = useState<'ACEPTADA' | 'RECHAZADA' | 'OTRA_FECHA'>('ACEPTADA');
  const [otraFecha, setOtraFecha] = useState('');
  const [nota, setNota] = useState('');

  const refrescar = (): void => {
    void cliente.invalidateQueries({ queryKey: ['cau'] });
    // Una respuesta del Exito cierra la solicitud, y la orden puede volver a la
    // lista de trabajo si sigue sin poder entregarse.
    void cliente.invalidateQueries({ queryKey: ['cau-candidatas'] });
    alTerminar();
  };

  const radicar = useMutation({
    mutationFn: () => marcarRadicada(solicitud.id, radicado.trim()),
    onSuccess: refrescar,
  });
  const responder = useMutation({
    mutationFn: () =>
      registrarRespuestaCau(solicitud.id, {
        estado,
        ...(estado === 'OTRA_FECHA' ? { fechaAprobada: otraFecha } : {}),
        ...(nota.trim() ? { respuesta: nota.trim() } : {}),
      }),
    onSuccess: refrescar,
  });

  return (
    <div className="space-y-3 text-sm">
      {solicitud.estado === 'BORRADOR' && (
        <p className="text-tenue">
          Todavía no se ha generado el archivo. Márcala arriba y genera el Excel.
        </p>
      )}

      {(solicitud.estado === 'GENERADA' || solicitud.estado === 'RADICADA') && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-tenue">Número de radicado de la PQR</span>
            <input
              value={radicado}
              onChange={(e) => setRadicado(e.target.value)}
              placeholder="el que da el Éxito al radicar"
              className="w-64 rounded-md border border-borde px-2 py-1.5 text-sm"
            />
          </label>
          <Boton
            onClick={() => radicar.mutate()}
            deshabilitado={radicado.trim() === '' || radicar.isPending}
          >
            {solicitud.estado === 'RADICADA' ? 'Corregir el radicado' : 'Ya la radiqué'}
          </Boton>
        </div>
      )}

      {solicitud.estado === 'RADICADA' && (
        <div className="flex flex-wrap items-end gap-2 border-t border-borde pt-3">
          <label className="block">
            <span className="mb-1 block text-xs text-tenue">Qué contestaron</span>
            <select
              value={estado}
              onChange={(e) => setEstado(e.target.value as typeof estado)}
              className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
            >
              <option value="ACEPTADA">Aceptaron la fecha</option>
              <option value="OTRA_FECHA">Dieron otra fecha</option>
              <option value="RECHAZADA">La rechazaron</option>
            </select>
          </label>
          {estado === 'OTRA_FECHA' && (
            <label className="block">
              <span className="mb-1 block text-xs text-tenue">Cuál</span>
              <input
                type="date"
                value={otraFecha}
                onChange={(e) => setOtraFecha(e.target.value)}
                className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              />
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-xs text-tenue">Nota — opcional</span>
            <input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              className="w-64 rounded-md border border-borde px-2 py-1.5 text-sm"
            />
          </label>
          <Boton
            onClick={() => responder.mutate()}
            deshabilitado={
              responder.isPending || (estado === 'OTRA_FECHA' && otraFecha === '')
            }
          >
            Guardar la respuesta
          </Boton>
        </div>
      )}

      {solicitud.respuesta && <p className="text-tenue">«{solicitud.respuesta}»</p>}
      {radicar.isError && <Error_>{(radicar.error as Error).message}</Error_>}
      {responder.isError && <Error_>{(responder.error as Error).message}</Error_>}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Generar el Excel, eligiendo con qué plantilla.
 *
 * Las dos son el mismo formato; lo que cambia es cuánto tarda. El original es el
 * que el Éxito ya sabe que pasa; el rápido conserva todo lo que revisan pero
 * **todavía no se ha radicado ninguno con él**. Por eso se elige, y por eso el
 * nombre del archivo dice cuál se usó.
 */
function Generar({ ids, alTerminar }: { ids: string[]; alTerminar: () => void }): ReactNode {
  const cliente = useQueryClient();
  const [formato, setFormato] = useState<FormatoCau>('original');

  const generar = useMutation({
    mutationFn: () => descargarCau(ids, formato),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['cau'] });
      // Cambia como se ve la orden en la lista de trabajo de arriba.
      void cliente.invalidateQueries({ queryKey: ['cau-candidatas'] });
      alTerminar();
    },
  });

  return (
    <Card titulo={`Generar el archivo · ${ids.length} ${ids.length === 1 ? 'orden' : 'órdenes'}`}>
      <div className="space-y-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            className="mt-1"
            checked={formato === 'original'}
            onChange={() => setFormato('original')}
          />
          <span>
            <strong>Formato original</strong> — idéntico al que el Éxito ya acepta.{' '}
            <span className="text-tenue">Tarda alrededor de medio minuto en generarse.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            className="mt-1"
            checked={formato === 'rapido'}
            onChange={() => setFormato('rapido')}
          />
          <span>
            <strong>Formato rápido</strong> — el mismo formato sin las 100.000 filas vacías.{' '}
            <span className="text-tenue">
              Instantáneo, y conserva encabezado, NIT y validaciones. Todavía no se ha
              radicado ninguno así.
            </span>
          </span>
        </label>

        {generar.isError && <Error_>{(generar.error as Error).message}</Error_>}

        <div className="flex flex-wrap items-center gap-2">
          <Boton onClick={() => generar.mutate()} deshabilitado={generar.isPending}>
            {generar.isPending ? 'Generando…' : 'Descargar el Excel'}
          </Boton>
          <Boton tipo="secundario" onClick={alTerminar}>
            Cancelar
          </Boton>
        </div>
        <p className="text-xs text-tenue">
          Descargarlo no lo radica. Después hay que subirlo a la PQR del Grupo Éxito y
          anotar aquí el número de radicado.
        </p>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function Formulario({
  ordenInicial,
  alTerminar,
}: {
  ordenInicial: string;
  alTerminar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const motivos = useQuery({ queryKey: ['cau-motivos'], queryFn: motivosCau });
  const ordenes = useQuery({
    queryKey: ['ordenes', 'para-cau'],
    queryFn: () => listarOrdenes({ tipo: 'YB1', tamano: '200' }),
  });

  const [ordenId, setOrdenId] = useState(ordenInicial);
  const [tipoSolicitud, setTipoSolicitud] = useState<TipoSolicitudCau>('PRORROGA');
  const [fechaPedida, setFechaPedida] = useState('');
  const [motivo, setMotivo] = useState('');
  const [comentario, setComentario] = useState('');

  const elegido: MotivoCau | undefined = (motivos.data ?? []).find((m) => m.texto === motivo);
  const esOtro = motivo === 'Otro';

  const guardar = useMutation({
    mutationFn: () =>
      crearProrroga({
        ordenId,
        tipoSolicitud,
        fechaPedida,
        motivo,
        ...(esOtro ? { comentario: comentario.trim() } : {}),
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['cau'] });
      // Cambia como se ve la orden en la lista de trabajo de arriba.
      void cliente.invalidateQueries({ queryKey: ['cau-candidatas'] });
      alTerminar();
    },
  });

  const listo =
    ordenId !== '' &&
    fechaPedida !== '' &&
    motivo !== '' &&
    (!esOtro || (comentario.trim().length >= 2 && comentario.trim().length <= 199));

  const candidatas = (ordenes.data?.datos ?? []).filter((o) => o.estado !== 'ANULADA');

  return (
    <Card titulo="Nueva solicitud">
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Orden">
          <select
            value={ordenId}
            onChange={(e) => setOrdenId(e.target.value)}
            className="w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="">Elige una orden del Éxito…</option>
            {candidatas.map((o) => (
              <option key={o.id} value={o.id}>
                {o.numero} · entrega {fecha(o.fechaEntregaMin)}
              </option>
            ))}
          </select>
          {/* El CAU es del Grupo Éxito: las de Cencosud van por otra vía. */}
          <span className="mt-1 block text-xs text-tenue">
            Solo órdenes del Éxito. Las de Cencosud tienen otra vía.
          </span>
        </Campo>

        <Campo etiqueta="Qué se pide">
          <select
            value={tipoSolicitud}
            onChange={(e) => setTipoSolicitud(e.target.value as TipoSolicitudCau)}
            className="w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="PRORROGA">Prórroga — entregar después</option>
            <option value="ANTICIPO ">Anticipo — entregar antes</option>
          </select>
        </Campo>

        <Campo etiqueta="Nueva fecha de entrega">
          <input
            type="date"
            value={fechaPedida}
            onChange={(e) => setFechaPedida(e.target.value)}
            className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          />
          {/* La plantilla no acepta fechas de más de tres días atrás. */}
          <span className="mt-1 block text-xs text-tenue">
            El formato no acepta fechas de más de tres días atrás.
          </span>
        </Campo>

        {/*
          La sublinea no se pregunta: para Alcanza siempre es 69 (confirmado por
          Pedro). Un dato que siempre vale lo mismo y que se teclea es un dato
          que algun dia se teclea mal, y la plantilla rechaza la fila entera.
        */}
        <Campo etiqueta="Sublínea">
          <div className="cifra w-24 rounded-md border border-borde bg-neutro-tenue px-2 py-1.5 text-sm text-tenue">
            69
          </div>
          <span className="mt-1 block text-xs text-tenue">
            Siempre es 69 para Alcanza. No se digita.
          </span>
        </Campo>

        <Campo etiqueta="Motivo">
          <select
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="">Elige un motivo…</option>
            {(motivos.data ?? []).map((m) => (
              <option key={m.texto} value={m.texto}>
                {m.texto.trim()}
              </option>
            ))}
          </select>
          {elegido && <span className="mt-1 block text-xs text-tenue">{elegido.cuando}</span>}
        </Campo>

        <Campo etiqueta="De quién es la responsabilidad">
          {/*
            No es un desplegable, y no lo va a ser: la responsabilidad sale del
            motivo. Si se pudiera elegir, alguien marcaría «Ninguno» un día con
            prisa y se perdería justo la prueba de que el incumplimiento fue del
            cliente.
          */}
          <div className="rounded-md border border-borde bg-fondo px-2 py-1.5 text-sm">
            {motivo === '' ? (
              <span className="text-tenue">— elige el motivo primero</span>
            ) : elegido?.responsabilidad ? (
              <strong>{NOMBRE_RESPONSABLE[elegido.responsabilidad]}</strong>
            ) : (
              <span className="italic text-tenue">sin asignar</span>
            )}
          </div>
          <span className="mt-1 block text-xs text-tenue">
            La pone el catálogo oficial a partir del motivo. No se elige.
          </span>
        </Campo>

        {esOtro && (
          <Campo etiqueta="Comentario — obligatorio con «Otro»">
            <input
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              maxLength={199}
              placeholder="entre 2 y 199 caracteres"
              className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
            />
          </Campo>
        )}
      </div>

      {guardar.isError && (
        <div className="mt-3">
          <Error_>{(guardar.error as Error).message}</Error_>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <Boton onClick={() => guardar.mutate()} deshabilitado={!listo || guardar.isPending}>
          Guardar
        </Boton>
        <Boton tipo="secundario" onClick={alTerminar}>
          Cancelar
        </Boton>
      </div>
    </Card>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: ReactNode }): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-tenue">{etiqueta}</span>
      {children}
    </label>
  );
}
